import { useState } from 'react';
import { IconChevronLeft, IconClockHour4, IconPlayerPause, IconPlus, IconSearch } from '@tabler/icons-react';
import { useNavigate } from '@tanstack/react-router';
import { HeaderTool, Page, PageHeader } from '@hana/shell';
import { Button, Segmented, Skeleton, StateView, useToast, type ScreenState } from '@hana/ui';
import { APPROVALS, BRIEF, DELEGS, DEL_TASKS, DONE, DONE_SEAT, TASKS, TEAM, filterTasks, groupTasks, type TaskRow } from './tasks';
import { useT } from '@hana/i18n';
import './i18n';
import s from './compact.module.css';

// Mint-Tasks-iPad (danh sách một cột) · Mint-Tasks-Phone (thẻ việc). Tab "Việc của tôi"; chỉ đọc, nút ghi báo toast.

const GROUP_DOT: Record<string, string> = { over: 'var(--err)', today: 'var(--d-blue)', wait: 'var(--d-gray)', next: 'var(--line)' };

function readState(): ScreenState | null {
  const v = new URLSearchParams(location.search).get('state');
  return v === 'loading' || v === 'empty' || v === 'error' || v === 'noperm' ? v : null;
}

function codeCls(c: string) {
  if (c === 'GPW' || c === 'DUN') return s.cErr;
  if (c === 'MEMO' || c === '@') return s.cMute;
  if (c === 'PRC' || c === 'DCF') return s.cPri;
  return '';
}
const timedDue = (x: TaskRow) => !x.wait && /^(Còn|Quá)\s/.test(x.dueK);
const dueText = (x: TaskRow) => (x.wait ? 'Dừng đồng hồ' : x.dueK.replace(/^Quá /, '−').replace(/^Còn /, ''));

function Tags({ x }: { x: TaskRow }) {
  const t = useT();
  return (
    <>
      {x.gate === 2 && <span className={`${s.tag} ${s.tErr}`}>{t('tasks.gateBlock')}</span>}
      {x.gate === 1 && <span className={`${s.tag} ${s.tWarn}`}>{t('tasks.gateWarn')}</span>}
      {x.ext > 0 && <span className={s.tag}>+4h {x.ext}/3</span>}
    </>
  );
}

interface OtherItem { id: number | string; code: string; title: string; obj: string; seat: string; due: string; tone: string; tags: string[] }
function otherGroups(tab: string): { label: string; dot: string; items: OtherItem[] }[] {
  if (tab === 'appr') {
    return [{ label: 'Chờ anh duyệt', dot: 'var(--d-blue)', items: APPROVALS.map((a) => ({ id: a.id, code: 'DUY', title: `${a.kindLabel} · ${a.title}`, obj: `đề nghị ${a.by}`, seat: a.steps[a.cur]?.[1] ?? '', due: '', tone: '', tags: [`Bước ${a.cur + 1}/${a.steps.length}`, ...a.flags.map((f) => f.k)] })) }];
  }
  if (tab === 'deleg') {
    return [
      { label: 'Uỷ quyền đang nhận', dot: 'var(--ok)', items: DELEGS.map((d) => ({ id: d.id, code: 'UQ', title: `Thay cho ${d.k}`, obj: d.scope, seat: d.range, due: 'Đang hiệu lực', tone: '', tags: [] })) },
      { label: 'Việc phát sinh trong kỳ', dot: 'var(--d-blue)', items: DEL_TASKS.map((d) => ({ id: d.id, code: d.code, title: d.title, obj: d.obj, seat: `thay cho ${d.forK}`, due: d.dueK, tone: d.tone, tags: [] })) },
    ];
  }
  if (tab === 'team') {
    return [{ label: 'Team KD & CS', dot: 'var(--d-blue)', items: TEAM.map((r) => ({ id: r.k, code: r.k.slice(0, 2), title: `${r.k} · ${r.open} việc mở`, obj: r.seat, seat: `quá hạn ${r.over}`, due: '', tone: '', tags: [`Đúng hạn ${r.ont}%`, ...(r.gate ? [`${r.gate} việc bị chặn`] : [])] })) }];
  }
  return [{ label: 'Đã xong 7 ngày', dot: 'var(--ok)', items: DONE.map((d) => ({ id: d.id, code: d.code, title: d.title, obj: d.obj, seat: `xong ${d.at}`, due: d.ont ? 'Đúng hạn' : 'Trễ hạn', tone: d.ont ? '' : 'err', tags: [], })) }].map((g) => ({ ...g, items: g.items.map((i) => ({ ...i, seat: i.seat || DONE_SEAT })) }));
}

export function TasksCompact({ phone }: { phone: boolean }) {
  const t = useT();
  const toast = useToast();
  const navigate = useNavigate();
  const forced = readState();
  const [q, setQ] = useState('');
  const [gateOnly, setGateOnly] = useState(false);
  const state: ScreenState = forced ?? 'ready';
  const noWrite = () => toast({ msg: 'Chưa mở ghi ở bản này' });
  const [tab, setTab] = useState('mine');
  const pickTab = (k: string) => setTab(k);
  const norm = (x: string) => x.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/đ/g, 'd').toLowerCase();
  const other = tab === 'mine' ? [] : otherGroups(tab).map((g) => ({ ...g, items: g.items.filter((i) => !q || norm(`${i.title} ${i.obj}`).includes(norm(q))) })).filter((g) => g.items.length);
  const rows = filterTasks(TASKS, { q, onlyGate: gateOnly, onlyMine: false, scr: 'all', job: '', codes: [] });
  const groups = groupTasks(rows);
  const open = (id: number) => void navigate({ to: '/ezy/tasks/$kind/$id', params: { kind: 'task', id: String(id) } });
  const tabs = [
    { key: 'mine', label: phone ? 'Của tôi' : 'Của tôi', n: TASKS.length },
    { key: 'appr', label: 'Duyệt', n: APPROVALS.length },
    { key: 'deleg', label: 'Đang thay', n: 2 },
    { key: 'team', label: 'Team', n: 6 },
    { key: 'done', label: phone ? 'Xong' : 'Đã xong', n: DONE.length },
  ];

  return (
    <Page>
      <PageHeader
        title="Việc & Duyệt"
        lead={<HeaderTool label="Về Trang chủ" icon={IconChevronLeft} onClick={() => void navigate({ to: '/ezy' })} />}
        tabs={phone ? undefined : <Segmented label="Nhóm việc" value={tab} onChange={pickTab} items={tabs} />}
        actions={<Button variant="primary" icon={<IconPlus size={16} aria-hidden="true" />} onClick={noWrite}>{phone ? '' : 'Việc'}</Button>}
      />
      {phone && <div className={s.phoneTabs}><Segmented label="Nhóm việc" value={tab} onChange={pickTab} items={tabs} /></div>}
      <StateView
        state={state}
        title={state === 'empty' ? 'Không còn việc nào' : undefined}
        desc={state === 'empty' ? 'Việc mới sinh tự động khi dữ liệu thay đổi.' : undefined}
        action={state === 'error' ? <Button onClick={() => location.reload()}>{t('tasks.retry')}</Button> : undefined}
        skeleton={<Skeleton rows={7} />}
      >
        <div className={phone ? s.phoneWrap : s.list}>
          {!phone && <div className={s.head}>
            <label className={s.search}>
              <IconSearch size={17} stroke={2.2} aria-hidden="true" />
              <input aria-label="Tìm" placeholder="Tìm việc, phiếu, job…" value={q} onChange={(e) => setQ(e.target.value)} />
            </label>
            <div className={s.chips}>
              <button type="button" className={s.chipBtn} aria-pressed={!gateOnly} onClick={() => setGateOnly(false)}>{t('tasks.all')}</button>
              <button type="button" className={s.chipBtn} aria-pressed={gateOnly} onClick={() => setGateOnly(true)}>{t('tasks.gateOnly')}</button>
            </div>
          </div>}
          {tab === 'mine' && <div className={s.brief}><b>Ask HANA{phone ? '' : ' · đầu ngày'}</b> — {BRIEF}</div>}
          {tab !== 'mine' && other.map((g) => (
            <section key={g.label} aria-label={g.label}>
              <div className={s.grp}><span className={s.dot} style={{ background: g.dot }} /><b>{g.label}</b><span>{g.items.length}</span></div>
              {g.items.map((x) => (
                <div key={x.id} className={phone ? s.card : s.row} >
                  <div className={phone ? s.cardTop : undefined} style={phone ? undefined : { display: 'contents' }}>
                    <span className={`${s.code} ${codeCls(x.code)}`}>{x.code}</span>
                    <div className={s.main}>
                      <div>{x.title}</div>
                      <div className={s.obj}>{x.obj} · {x.seat}</div>
                      {(x.due || x.tags.length > 0) && (
                        <div className={s.meta}>
                          {x.due && <span className={`${s.due} ${x.tone === 'err' ? s.err : ''}`}>{x.due}</span>}
                          {x.tags.map((tg) => <span key={tg} className={s.tag}>{tg}</span>)}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </section>
          ))}
          {tab === 'mine' && groups.map((g) => (
            <section key={g.key} aria-label={g.label}>
              <div className={s.grp}><span className={s.dot} style={{ background: GROUP_DOT[g.key] }} /><b className={g.key === 'over' ? s.err : undefined}>{g.label}</b><span>{g.items.length}</span></div>
              {g.items.map((x) => phone ? (
                <div key={x.id} className={s.card} onClick={() => open(x.id)}>
                  <div className={s.cardTop}>
                    <span className={`${s.code} ${codeCls(x.code)}`}>{x.code}</span>
                    <div className={s.main}><div>{x.title}</div><div className={s.obj}>{x.obj || '—'}</div></div>
                  </div>
                  <div className={s.meta}>
                    <span className={`${s.due} ${g.key === 'over' ? s.err : ''}`}>{timedDue(x) && <IconClockHour4 size={13} stroke={2} aria-hidden="true" />}{dueText(x)}</span>
                    <span className={s.seat}>· {x.seat}</span>
                    <Tags x={x} />
                  </div>
                  <div className={s.acts}>
                    <button type="button" className={s.sq} aria-label="Chờ bên ngoài" onClick={(e) => { e.stopPropagation(); noWrite(); }}><IconPlayerPause size={16} aria-hidden="true" /></button>
                    <button type="button" className={s.ext} aria-label="Gia hạn 4 giờ" disabled={x.noExt || x.ext >= 3} onClick={(e) => { e.stopPropagation(); noWrite(); }}><IconPlus size={16} aria-hidden="true" />4h</button>
                    <button type="button" className={s.fin} onClick={(e) => { e.stopPropagation(); noWrite(); }}>Xong</button>
                  </div>
                </div>
              ) : (
                <div key={x.id} className={s.row} onClick={() => open(x.id)}>
                  <span className={`${s.code} ${codeCls(x.code)}`}>{x.code}</span>
                  <div className={s.main}>
                    <div>{x.title}</div>
                    <div className={s.obj}>{x.obj || '—'} · {x.seat}</div>
                    <div className={s.meta}>
                      <span className={`${s.due} ${g.key === 'over' ? s.err : ''}`}>{timedDue(x) && <IconClockHour4 size={13} stroke={2} aria-hidden="true" />}{dueText(x)}</span>
                      <Tags x={x} />
                    </div>
                  </div>
                </div>
              ))}
            </section>
          ))}
          {(tab === 'mine' ? groups.length === 0 : other.length === 0) && <div className={s.none}>{t('tasks.noMatch')}</div>}
        </div>
      </StateView>
    </Page>
  );
}
